'use client';

/**
 * LoyaltyAppHero — V3 hero for /loyalty-app.
 *
 * The companion staff app that turns every customer interaction into
 * a 5-second QR scan. Frames the invisible problem before naming the
 * product: punching loyalty data into a tablet POS slows the line
 * and breaks customer flow.
 */

import Image from 'next/image';
import React from 'react';
import { Locale } from '../../../../i18n-config';
import { useTranslation } from '@/i18n/client';
import FeatureHero from '../_shared/sections/FeatureHero';

type Props = { lang: Locale };

const LOYALTY_APP_IMAGE = 'https://cdn.1cdn.app/application/UMD/26050207472730_lp_app.png';

const LoyaltyAppHero = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  const visual = (
    <Image
      src={LOYALTY_APP_IMAGE}
      alt={t('LoyaltyApp/Preview/image_alt')}
      width={840}
      height={840}
      sizes="(min-width: 1024px) 320px, 70vw"
      className="mx-auto h-auto w-3/4 max-w-[320px] rounded-3xl lg:w-full"
      priority
    />
  );

  return (
    <FeatureHero
      accent="#22D3EE"
      ariaLabel={t('LoyaltyApp/Hero/aria_label')}
      copy={{
        kicker: t('LoyaltyApp/Hero/badge'),
        headlineA: t('LoyaltyApp/Hero/title1'),
        headlineB: t('LoyaltyApp/Hero/title2'),
        subhead: t('LoyaltyApp/Hero/subtitle'),
        ctaPrimary: {
          label: t('LoyaltyApp/Hero/cta_primary'),
          href: `${process.env.NEXT_PUBLIC_DASHBOARD_BASE_URL}/register`,
        },
        ctaSecondary: {
          label: t('LoyaltyApp/Hero/cta_secondary'),
          href: '#preview',
        },
        trust: [t('LoyaltyApp/Hero/trust_byod'), t('LoyaltyApp/Hero/trust_roles'), t('LoyaltyApp/Hero/trust_hardware')],
      }}
      visual={visual}
    />
  );
};

export default LoyaltyAppHero;
